
import React, { useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useMoneyInput } from '@/hooks/use-money-input';

interface MoneyInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
}

export function MoneyInput({ 
  value, 
  onChange, 
  className,
  placeholder = "0.00"
}: MoneyInputProps) {
  const { formatNumber, parseNumber, calculateCursorPosition } = useMoneyInput();
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const previousValueRef = useRef<string>('');

  // Track input changes while preserving cursor position
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputElement = event.target;
    const currentCursorPosition = inputElement.selectionStart;
    const currentValue = inputElement.value;
    
    // Store cursor position and current value for later use
    cursorPositionRef.current = currentCursorPosition;
    previousValueRef.current = currentValue;
    
    // Process the value
    const numValue = parseNumber(currentValue);
    onChange(numValue);
  };

  // After the value changes and is formatted, restore cursor position
  useEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null) {
      const formattedValue = formatNumber(value);
      
      // Only adjust cursor if we have a previous value to compare with
      if (previousValueRef.current) {
        const newCursorPosition = calculateCursorPosition(
          previousValueRef.current,
          formattedValue,
          cursorPositionRef.current
        );
        
        // Set the cursor position after the component renders
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        }, 0);
      }
    }
  }, [value, formatNumber, calculateCursorPosition]);

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
      <Input
        ref={inputRef}
        type="text"
        value={formatNumber(value)}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn("pl-8", className)}
      />
    </div>
  );
}
